import React from 'react'

// Chart-type pictograms as vectors rather than a sprite sheet.
//
// The sheet these replace had ~118px of usable artwork per cell sitting in a
// 258px-wide box: it filled 46% of its tile and could not be sharpened, since
// 118px was all the resolution the source contained. Drawn as SVG they are
// crisp at any density, fill the tile by construction, take their ink from
// `currentColor`, and cost a few KB in the JS bundle instead of a 50KB PNG.
//
// One shared 120x84 viewBox. Axis-based glyphs plot inside x 17..112,
// y 10..72; full-bleed ones use the whole box. Ink is currentColor, secondary
// fills read --glyph-pale, so a theme can retune both from CSS.

const PALE = 'var(--glyph-pale, #cfeef0)'

const Axes = () => (
  <g stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" fill="none">
    <path d="M18 10V72H112" />
    <path d="M14.2 14.4 18 10l3.8 4.4" />
    <path d="M107.6 68.2 112 72l-4.4 3.8" />
  </g>
)

const dots = (pts, r = 3.4) =>
  pts.map(([x, y]) => <circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill="currentColor" />)

const polar = (cx, cy, r, deg) => [
  +(cx + r * Math.cos((deg * Math.PI) / 180)).toFixed(1),
  +(cy + r * Math.sin((deg * Math.PI) / 180)).toFixed(1),
]

// A violin is two mirrored density lobes; built symmetrically so the halves
// cannot drift apart.
const violin = (cx, w) =>
  `M${cx} 11C${cx + w} 23 ${cx + w} 31 ${cx} 41c${w} 10 ${w} 20 0 32` +
  `c${-w} -12 ${-w} -22 0 -32C${cx - w} 31 ${cx - w} 23 ${cx} 11Z`

// Ordered to match the chart registry in src/charts.js. `name` is carried so a
// reorder upstream is caught in development rather than silently mismatching.
const GLYPHS = [
  {
    name: 'Alluvial Diagram',
    draw: (
      <>
        <path d="M29 13c28 0 38 17 66 17v15c-28 0-38-17-66-17z" fill={PALE} />
        <path d="M29 34c28 0 38 27 66 27v12c-28 0-38-27-66-27z" fill={PALE} />
        <rect x="18" y="11" width="10" height="20" fill="currentColor" />
        <rect x="18" y="35" width="10" height="28" fill="currentColor" />
        <rect x="95" y="27" width="10" height="24" fill="currentColor" />
        <rect x="95" y="56" width="10" height="18" fill="currentColor" />
      </>
    ),
  },
  {
    name: 'Arc Diagram',
    draw: (
      <>
        <g stroke="currentColor" strokeWidth="2.3" fill="none">
          <path d="M22 62a15 15 0 0 1 30 0" />
          <path d="M37 62a22 22 0 0 1 44 0" />
          <path d="M66 62a17 17 0 0 1 34 0" />
        </g>
        <g>{dots([[22, 62], [37, 62], [52, 62], [66, 62], [81, 62], [100, 62]], 3.2)}</g>
      </>
    ),
  },
  {
    name: 'Bar chart',
    draw: (
      <>
        <Axes />
        <rect x="31" y="42" width="18" height="30" fill="currentColor" />
        <rect x="57" y="24" width="18" height="48" fill={PALE} />
        <rect x="83" y="33" width="18" height="39" fill="currentColor" />
      </>
    ),
  },
  {
    name: 'Multi-set bar chart',
    draw: (
      <>
        <Axes />
        <rect x="29" y="40" width="9" height="32" fill="currentColor" />
        <rect x="40" y="28" width="9" height="44" fill={PALE} />
        <rect x="58" y="47" width="9" height="25" fill="currentColor" />
        <rect x="69" y="34" width="9" height="38" fill={PALE} />
        <rect x="87" y="22" width="9" height="50" fill="currentColor" />
        <rect x="98" y="44" width="9" height="28" fill={PALE} />
      </>
    ),
  },
  {
    name: 'Stacked bar chart',
    draw: (
      <>
        <Axes />
        <rect x="32" y="52" width="18" height="20" fill="currentColor" />
        <rect x="32" y="36" width="18" height="15" fill={PALE} />
        <rect x="59" y="44" width="18" height="28" fill="currentColor" />
        <rect x="59" y="22" width="18" height="21" fill={PALE} />
        <rect x="86" y="56" width="18" height="16" fill="currentColor" />
        <rect x="86" y="33" width="18" height="22" fill={PALE} />
      </>
    ),
  },
  {
    name: 'Beeswarm plot',
    draw: (
      <>
        <Axes />
        <g>
          {dots(
            [
              [30, 42], [38, 32], [38, 52], [46, 24], [46, 42], [46, 60],
              [56, 34], [56, 50], [66, 22], [66, 40], [66, 58], [76, 32],
              [76, 50], [88, 38], [88, 56], [100, 44],
            ],
            4
          )}
        </g>
      </>
    ),
  },
  {
    name: 'Box plot',
    draw: (
      <>
        <Axes />
        <g stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <path d="M38 18v10M38 56v10M63 24v9M63 60v8M88 14v11M88 52v10" />
        </g>
        <rect x="29" y="28" width="18" height="28" fill={PALE} stroke="currentColor" strokeWidth="2.2" />
        <rect x="54" y="33" width="18" height="27" fill={PALE} stroke="currentColor" strokeWidth="2.2" />
        <rect x="79" y="25" width="18" height="27" fill={PALE} stroke="currentColor" strokeWidth="2.2" />
        <g stroke="currentColor" strokeWidth="2.4">
          <path d="M29 42h18M54 47h18M79 38h18" />
        </g>
      </>
    ),
  },
  {
    name: 'Bubble chart',
    draw: (
      <>
        <Axes />
        <circle cx="40" cy="52" r="8" fill={PALE} />
        <circle cx="62" cy="32" r="13" fill="currentColor" />
        <circle cx="86" cy="55" r="10" fill={PALE} />
        <circle cx="99" cy="30" r="6" fill="currentColor" />
      </>
    ),
  },
  {
    name: 'Bumpchart',
    draw: (
      <>
        <path d="M18 30c22 0 26 26 47 26s25-24 47-24v14c-22 0-26 24-47 24s-25-26-47-26z" fill={PALE} />
        <g stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round">
          <path d="M18 22c22 0 26 30 47 30s25-28 47-28" />
          <path d="M18 62c22 0 26-26 47-26s25 22 47 22" />
        </g>
      </>
    ),
  },
  {
    name: 'Calendar heatmap',
    draw: (
      <>
        {[0, 1, 2, 3, 4, 5, 6, 7].map((c) =>
          [0, 1, 2, 3, 4].map((r) => {
            const v = (c * 7 + r * 3) % 5
            return (
              <rect
                key={`${c}-${r}`}
                x={16 + c * 12.4}
                y={12 + r * 12.4}
                width="10"
                height="10"
                rx="1.6"
                fill={v === 0 ? 'currentColor' : v === 1 ? PALE : v === 2 ? 'currentColor' : PALE}
                opacity={v === 0 ? 1 : v === 2 ? 0.55 : 1}
              />
            )
          })
        )}
      </>
    ),
  },
  {
    name: 'Chord Diagram',
    draw: (
      <>
        <circle cx="60" cy="42" r="30" fill="none" stroke="currentColor" strokeWidth="5" strokeDasharray="26 7" />
        <g stroke="currentColor" strokeWidth="2.4" fill="none">
          <path d="M38 24c14 18 30 22 44 8" />
          <path d="M34 54c22 6 32-2 40-24" />
          <path d="M52 71c6-20 20-28 34-22" />
        </g>
      </>
    ),
  },
  {
    name: 'Circle Packing',
    draw: (
      <>
        <circle cx="60" cy="42" r="31" fill={PALE} />
        <circle cx="49" cy="33" r="11" fill="currentColor" />
        <circle cx="72" cy="30" r="7" fill="currentColor" />
        <circle cx="70" cy="52" r="13" fill="currentColor" />
        <circle cx="45" cy="55" r="6" fill="currentColor" />
      </>
    ),
  },
  {
    name: 'Circular dendrogram',
    draw: (() => {
      const cx = 60
      const cy = 42
      const mid = [-150, -90, -30, 30, 90, 150]
      const inner = mid.map((a) => polar(cx, cy, 17, a))
      const leaves = mid.flatMap((a) => [polar(cx, cy, 33, a - 13), polar(cx, cy, 33, a + 13)])
      return (
        <>
          <g stroke="currentColor" strokeWidth="1.9" fill="none">
            {inner.map(([x, y], i) => (
              <path key={`s${i}`} d={`M${cx} ${cy}L${x} ${y}`} />
            ))}
            {inner.map(([x, y], i) => (
              <path
                key={`b${i}`}
                d={`M${x} ${y}L${leaves[i * 2][0]} ${leaves[i * 2][1]}M${x} ${y}L${leaves[i * 2 + 1][0]} ${leaves[i * 2 + 1][1]}`}
              />
            ))}
          </g>
          <g>{dots(leaves, 2.7)}</g>
          <g>{dots(inner, 3.2)}</g>
          <circle cx={cx} cy={cy} r="4.4" fill="currentColor" />
        </>
      )
    })(),
  },
  {
    name: 'Contour plot',
    draw: (
      <>
        <Axes />
        <path d="M36 30c18-14 44-10 52 6s-10 32-30 30-34-22-22-36z" fill={PALE} />
        <g fill="none" stroke="currentColor" strokeWidth="2.2">
          <path d="M45 36c12-9 30-6 35 4s-7 21-20 20-23-15-15-24z" />
          <path d="M55 43c6-4 15-3 17 2s-3 10-10 10-11-8-7-12z" />
        </g>
      </>
    ),
  },
  {
    name: 'Convex hull',
    draw: (
      <>
        <Axes />
        <path d="M40 26 78 18l24 26-16 26-38 4z" fill={PALE} stroke="currentColor" strokeWidth="2.3" />
        <g>{dots([[40, 26], [78, 18], [102, 44], [86, 70], [48, 74], [62, 42], [74, 56]], 3.2)}</g>
      </>
    ),
  },
  {
    name: 'Linear dendrogram',
    draw: (
      <>
        <g stroke="currentColor" strokeWidth="2.2" fill="none">
          <path d="M18 42h18M36 20v44M36 20h20M36 64h20M56 12v16M56 12h22M56 28h22M56 56v16M56 56h22M56 72h22" />
        </g>
        <g>{dots([[18, 42]], 4)}</g>
        <g>{dots([[78, 12], [78, 28], [78, 56], [78, 72]], 3.4)}</g>
      </>
    ),
  },
  {
    name: 'Gantt chart',
    draw: (
      <>
        <g stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <path d="M18 74h94" />
        </g>
        <rect x="22" y="12" width="42" height="10" rx="3" fill="currentColor" />
        <rect x="40" y="27" width="46" height="10" rx="3" fill={PALE} />
        <rect x="58" y="42" width="38" height="10" rx="3" fill="currentColor" />
        <rect x="30" y="57" width="34" height="10" rx="3" fill={PALE} />
      </>
    ),
  },
  {
    name: 'Hexagonal binning',
    draw: (
      <>
        <Axes />
        {/* Proper honeycomb: columns step 1.5r across and half a cell down. */}
        {[
          [42, 26, 'currentColor'],
          [42, 50, PALE],
          [62, 14, PALE],
          [62, 38, 'currentColor'],
          [62, 62, 'currentColor'],
          [82, 26, 'currentColor'],
          [82, 50, PALE],
          [102, 38, PALE],
        ].map(([cx, cy, fill]) => {
          const r = 12.4
          const hw = (r * Math.sqrt(3)) / 2
          return (
            <polygon
              key={`${cx}-${cy}`}
              points={`${cx},${cy - r} ${cx + hw},${cy - r / 2} ${cx + hw},${cy + r / 2} ${cx},${cy + r} ${cx - hw},${cy + r / 2} ${cx - hw},${cy - r / 2}`}
              fill={fill}
            />
          )
        })}
      </>
    ),
  },
  {
    name: 'Horizon graph',
    draw: (
      <>
        <g stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <path d="M12 70h96" />
        </g>
        <path d="M12 70c8-24 18-24 26 0z" fill={PALE} />
        <path d="M38 70c10-34 22-34 32 0z" fill="currentColor" />
        <path d="M70 70c8-20 16-20 24 0z" fill={PALE} />
        <path d="M94 70c4-12 10-12 14 0z" fill="currentColor" />
      </>
    ),
  },
  {
    name: 'Line chart',
    draw: (
      <>
        <Axes />
        <polyline
          points="26,58 42,44 56,50 70,28 86,36 104,18"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <g>{dots([[26, 58], [42, 44], [56, 50], [70, 28], [86, 36], [104, 18]], 3.2)}</g>
      </>
    ),
  },
  {
    name: 'Matrix Plot',
    draw: (
      <>
        {[0, 1, 2, 3, 4].map((c) =>
          [0, 1, 2, 3].map((r) => {
            const s = 4 + ((c * 3 + r * 5) % 7)
            const cx = 22 + c * 19
            const cy = 17 + r * 16
            return (
              <rect
                key={`${c}-${r}`}
                x={cx - s / 2}
                y={cy - s / 2}
                width={s}
                height={s}
                rx="1.4"
                fill={(c + r) % 2 ? PALE : 'currentColor'}
              />
            )
          })
        )}
      </>
    ),
  },
  {
    name: 'Parallel coordinates',
    draw: (
      <>
        <g stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <path d="M24 12v60M60 12v60M96 12v60" />
        </g>
        <g fill="none" strokeWidth="2.8" strokeLinejoin="round" strokeLinecap="round">
          <path d="M24 24 60 44 96 22" stroke={PALE} />
          <path d="M24 40 60 20 96 50" stroke="currentColor" />
          <path d="M24 62 60 56 96 64" stroke={PALE} />
        </g>
        <g>{dots([[24, 40], [60, 20], [96, 50]], 3.2)}</g>
      </>
    ),
  },
  {
    name: 'Pie chart',
    draw: (
      <>
        <circle cx="60" cy="42" r="31" fill="currentColor" />
        <path d="M60 42V11a31 31 0 0 1 26.8 15.5z" fill={PALE} />
        <path d="M60 42 33 57.5A31 31 0 0 1 33 26.5z" fill={PALE} opacity="0.55" />
      </>
    ),
  },
  {
    name: 'Point map',
    draw: (
      <>
        <ellipse cx="60" cy="42" rx="34" ry="26" fill="none" stroke="currentColor" strokeWidth="2.4" />
        <g stroke="currentColor" strokeWidth="2" fill="none" opacity="0.5">
          <path d="M26 42h68M60 16c10 10 10 42 0 52M60 16c-10 10-10 42 0 52" />
        </g>
        <g>{dots([[46, 32], [72, 36], [58, 54], [84, 50]], 4)}</g>
      </>
    ),
  },
  {
    name: 'Radar Chart',
    draw: (
      <>
        <polygon points="60,10 105,40 88,72 32,72 15,40" fill="none" stroke="currentColor" strokeWidth="2.2" opacity="0.45" />
        <polygon points="60,26 88,44 78,64 42,64 32,44" fill={PALE} />
        <polygon points="60,20 96,42 72,68 46,58 38,38" fill="none" stroke="currentColor" strokeWidth="2.8" />
        <g>{dots([[60, 20], [96, 42], [72, 68], [46, 58], [38, 38]], 3.2)}</g>
      </>
    ),
  },
  {
    name: 'Sankey Diagram',
    draw: (
      <>
        <path d="M28 12c30 0 34 14 64 14v14c-30 0-34-14-64-14z" fill={PALE} />
        <path d="M28 44c30 0 34 20 64 20v10c-30 0-34-20-64-20z" fill={PALE} opacity="0.7" />
        <rect x="18" y="10" width="10" height="24" fill="currentColor" />
        <rect x="18" y="42" width="10" height="30" fill="currentColor" />
        <rect x="92" y="24" width="10" height="20" fill="currentColor" />
        <rect x="92" y="60" width="10" height="16" fill="currentColor" />
      </>
    ),
  },
  {
    name: 'Slope chart',
    draw: (
      <>
        <g stroke="currentColor" strokeWidth="2.1" opacity="0.45">
          <path d="M28 12v60M100 12v60" />
        </g>
        <g stroke="currentColor" strokeWidth="3" strokeLinecap="round">
          <path d="M28 22 100 62" />
          <path d="M28 42 100 20" />
          <path d="M28 64 100 44" />
        </g>
        <g>{dots([[28, 22], [28, 42], [28, 64], [100, 62], [100, 20], [100, 44]], 3.4)}</g>
      </>
    ),
  },
  {
    name: 'Streamgraph (area chart)',
    draw: (
      <>
        <path d="M12 44c18-16 30 10 48 2s30-18 48-6v16c-18-12-30 2-48 10s-30-18-48-2z" fill={PALE} />
        <path d="M12 56c18-10 30 8 48 2s30-12 48-4v10c-18-8-30 4-48 8s-30-12-48-4z" fill="currentColor" />
        <path d="M12 34c18-14 30 6 48 0s30-14 48-4v8c-18-10-30 4-48 10s-30-14-48-2z" fill="currentColor" opacity="0.45" />
      </>
    ),
  },
  {
    name: 'Sunburst diagram',
    draw: (
      <>
        {/* Concentric dashed rings read as segmented tiers far more reliably
            at this size than hand-computed wedge arcs. */}
        <circle cx="60" cy="42" r="9" fill="currentColor" />
        <circle
          cx="60"
          cy="42"
          r="18.5"
          fill="none"
          stroke={PALE}
          strokeWidth="11"
          strokeDasharray="26 5"
        />
        <circle
          cx="60"
          cy="42"
          r="30"
          fill="none"
          stroke="currentColor"
          strokeWidth="11"
          strokeDasharray="17 5"
        />
      </>
    ),
  },
  {
    name: 'Treemap',
    draw: (
      <>
        <rect x="14" y="12" width="46" height="36" fill="currentColor" />
        <rect x="63" y="12" width="43" height="22" fill={PALE} />
        <rect x="63" y="37" width="43" height="11" fill="currentColor" opacity="0.5" />
        <rect x="14" y="51" width="27" height="21" fill={PALE} />
        <rect x="44" y="51" width="30" height="21" fill="currentColor" opacity="0.7" />
        <rect x="77" y="51" width="29" height="21" fill={PALE} opacity="0.7" />
      </>
    ),
  },
  {
    name: 'Violin plot',
    draw: (
      <>
        <Axes />
        <path d={violin(38, 11)} fill={PALE} stroke="currentColor" strokeWidth="1.8" />
        <path d={violin(66, 14)} fill="currentColor" />
        <path d={violin(94, 9)} fill={PALE} stroke="currentColor" strokeWidth="1.8" />
      </>
    ),
  },
  {
    name: 'Voronoi Diagram',
    draw: (
      <>
        {/* The cells need an outer boundary, or the internal edges read as
            loose scratches rather than regions. */}
        <rect x="14" y="10" width="92" height="64" rx="3" fill={PALE} opacity="0.45" />
        <g stroke="currentColor" strokeWidth="2.1" fill="none" strokeLinejoin="round">
          <rect x="14" y="10" width="92" height="64" rx="3" />
          <path d="M14 34h30l16-24M44 34 41 74M44 34l32 10M76 44l16-34M76 44 70 74M76 44h30" />
        </g>
        <g>{dots([[28, 20], [60, 26], [27, 58], [57, 60], [92, 26], [93, 60]], 3.2)}</g>
      </>
    ),
  },
  {
    name: 'Treemap (Voronoi)',
    draw: (
      <>
        {/* Four cells that actually tile the box, sharing edges. */}
        <path d="M14 12 58 9l8 31-52 8z" fill="currentColor" />
        <path d="M58 9l48 5-6 28-34-2z" fill={PALE} />
        <path d="M14 51l52-8 6 31-46 2z" fill={PALE} opacity="0.72" />
        <path d="M66 43l34 2 6 28-34 1z" fill="currentColor" opacity="0.55" />
      </>
    ),
  },
]

export const GLYPH_COUNT = GLYPHS.length
export const GLYPH_VIEWBOX = '0 0 120 84'
export const GLYPH_RATIO = '120 / 84'

export function glyphNameAt(index) {
  return GLYPHS[index] ? GLYPHS[index].name : null
}

export default function ChartGlyph({ index, className }) {
  const glyph = GLYPHS[index]
  if (!glyph) return null
  return (
    <svg
      className={className}
      viewBox={GLYPH_VIEWBOX}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      {glyph.draw}
    </svg>
  )
}
